/**
 * Validação de imagem anexada pelo usuário.
 *
 * Vive aqui, e não em cada app, por um motivo concreto: a lista de tipos
 * aceitos espelha o `allowed_mime_types` do bucket `support-attachments`
 * (migration 20260810143851), e o teto de tamanho espelha o limite do mesmo
 * bucket. Duas cópias dessas constantes divergem — e a divergência só aparece
 * na cara do usuário, como um erro de storage que não diz nada.
 *
 * Sem React e sem i18next de propósito: quem chama decide a frase do erro, e
 * recebe só o código (`'tipo'` | `'tamanho'`).
 *
 * A CAPTURA automática de tela (html2canvas) NÃO mora aqui — ela é do CRM, é
 * pesada e é carregada sob demanda. Ver `apps/crm/src/lib/screenshot.ts`.
 */

/** Espelha `allowed_mime_types` do bucket (migration 20260810143851). */
export const MIMES_ACEITOS = ["image/png", "image/jpeg", "image/webp"] as const;

/** Para o `accept` do `<input type="file">`. */
export const ACCEPT_IMAGEM = MIMES_ACEITOS.join(",");

/** Teto do bucket é 5 MB; paramos antes para não descobrir isso no upload. */
export const TAMANHO_MAX_BYTES = 4 * 1024 * 1024;

/** Extensão do arquivo no bucket, por tipo aceito. */
export const EXTENSAO_POR_MIME: Record<string, string> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
};

export interface ImagemEscolhida {
  blob: Blob;
  /** object URL para a miniatura. Quem chama é responsável por revogar. */
  previewUrl: string;
  largura: number;
  altura: number;
  /** Tipo real da imagem — o upload precisa dele para não gravar tudo como PNG. */
  mime: string;
}

export type ErroImagem = "tipo" | "tamanho";

/**
 * Resultado da escolha. Um objeto com os dois campos, e não uma união
 * discriminada: o `tsconfig` do CRM roda com `strictNullChecks: false`, e sem
 * modo estrito o TypeScript não estreita `{ok:true}|{ok:false}` pelo `ok`.
 */
export interface ResultadoImagem {
  captura: ImagemEscolhida | null;
  erro: ErroImagem | null;
}

/**
 * Valida e embrulha o arquivo escolhido.
 *
 * A validação acontece aqui, e não só no `accept` do input: `accept` é dica de
 * UI, não regra — o usuário pode arrastar qualquer arquivo, e o bucket recusa
 * com um erro de storage que não diz nada a quem está reportando um problema.
 */
export async function imagemDoArquivo(file: File): Promise<ResultadoImagem> {
  if (!MIMES_ACEITOS.includes(file.type as (typeof MIMES_ACEITOS)[number])) {
    return { captura: null, erro: "tipo" };
  }
  if (file.size > TAMANHO_MAX_BYTES) {
    return { captura: null, erro: "tamanho" };
  }

  const previewUrl = URL.createObjectURL(file);
  // Dimensão é só informativa; se a leitura falhar não vale derrubar o anexo.
  const { largura, altura } = await medir(previewUrl).catch(() => ({
    largura: 0,
    altura: 0,
  }));

  return {
    captura: { blob: file, previewUrl, largura, altura, mime: file.type },
    erro: null,
  };
}

function medir(url: string): Promise<{ largura: number; altura: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () =>
      resolve({ largura: img.naturalWidth, altura: img.naturalHeight });
    img.onerror = reject;
    img.src = url;
  });
}
